import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader2, Play, Settings, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { authAPI, isPlatformAdmin } from '../lib/auth';

const STATUS_STYLES = {
  synced: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Synced' },
  syncing: { icon: RefreshCw, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Syncing' },
  failed: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Failed' },
  pending: { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-50', label: 'Pending' },
};

const MODULE_LABELS = {
  risk50: '50 Risk Checks',
  content_compliance: 'Content Compliance',
  merchant_underwriting: 'Merchant Underwriting',
  news_media: 'News & Media',
  payment_risk: 'Payment Risk',
  ocr_scan: 'OCR Scan',
};

export default function ModuleSyncPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggering, setTriggering] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const isAdmin = isPlatformAdmin();

  const loadModules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.listModuleSync();
      setModules(res.data.modules || []);
    } catch {
      setError('Failed to load module sync status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  const handleTrigger = async (id) => {
    setTriggering(id);
    try {
      await authAPI.triggerModuleSync(id);
      loadModules();
    } catch {
      setError('Failed to trigger sync');
    } finally {
      setTriggering(null);
    }
  };

  const startEdit = (mod) => {
    setEditingId(mod.id);
    setEditForm({
      endpoint_url: mod.endpoint_url || '',
      sync_interval_seconds: mod.sync_interval_seconds || 3600,
      is_enabled: mod.is_enabled !== false,
    });
  };

  const saveEdit = async () => {
    try {
      await authAPI.configureModuleSync(editingId, editForm);
      setEditingId(null);
      loadModules();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Module Sync</h1>
        </div>
        <button onClick={loadModules} className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : modules.length === 0 ? (
        <div className="text-center py-20 space-y-3">
          <RefreshCw className="w-12 h-12 text-gray-300 mx-auto" />
          <p className="text-gray-400">No module sync configurations found</p>
          <p className="text-xs text-gray-400">Platform admins can seed default entries from the tenant management page</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {modules.map(mod => {
            const s = STATUS_STYLES[mod.sync_status] || STATUS_STYLES.pending;
            const StatusIcon = s.icon;
            const isEdit = editingId === mod.id;
            return (
              <div key={mod.id} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {MODULE_LABELS[mod.module_name] || mod.module_name}
                    </h3>
                    {mod.tenant_name && <div className="text-xs text-gray-400">{mod.tenant_name}</div>}
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.color}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {s.label}
                  </div>
                </div>

                {isEdit ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Endpoint URL</label>
                      <input value={editForm.endpoint_url}
                        onChange={e => setEditForm(p => ({ ...p, endpoint_url: e.target.value }))}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Interval (seconds)</label>
                      <input type="number" value={editForm.sync_interval_seconds}
                        onChange={e => setEditForm(p => ({ ...p, sync_interval_seconds: parseInt(e.target.value) || 3600 }))}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={editForm.is_enabled}
                        onChange={e => setEditForm(p => ({ ...p, is_enabled: e.target.checked }))} className="rounded" />
                      Enabled
                    </label>
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700">Save</button>
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Endpoint</span>
                        <span className="text-gray-700 text-xs truncate max-w-[180px]">{mod.endpoint_url || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Enabled</span>
                        <span className={mod.is_enabled ? 'text-green-600' : 'text-gray-400'}>{mod.is_enabled ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Config Version</span>
                        <span className="text-gray-700">v{mod.config_version || 1}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Last Sync</span>
                        <span className="text-gray-700 text-xs">
                          {mod.last_sync_at ? new Date(mod.last_sync_at).toLocaleString() : 'Never'}
                        </span>
                      </div>
                    </div>

                    {isAdmin && (
                      <div className="flex gap-2 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => handleTrigger(mod.id)}
                          disabled={triggering === mod.id || !mod.is_enabled}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 disabled:opacity-40"
                        >
                          {triggering === mod.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                          Sync Now
                        </button>
                        <button
                          onClick={() => startEdit(mod)}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50"
                        >
                          <Settings className="w-3 h-3" /> Configure
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
